/**
 * Licensed Materials - Property of IBM
 * (c) Copyright IBM Corporation 2016, 2025. All Rights Reserved.
 *
 * Note to U.S. Government Users Restricted Rights:
 * Use, duplication or disclosure restricted by GSA ADP Schedule
 * Contract with IBM Corp.
 */
/*global module require*/
"use strict";

function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _classCallCheck(a, n) { if (!(a instanceof n)) throw new TypeError("Cannot call a class as a function"); }
function _defineProperties(e, r) { for (var t = 0; t < r.length; t++) { var o = r[t]; o.enumerable = o.enumerable || !1, o.configurable = !0, "value" in o && (o.writable = !0), Object.defineProperty(e, _toPropertyKey(o.key), o); } }
function _createClass(e, r, t) { return r && _defineProperties(e.prototype, r), t && _defineProperties(e, t), Object.defineProperty(e, "prototype", { writable: !1 }), e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
var config = require("../../../lib/shared/config"),
  Log = require("../../../lib/client/setup/log"),
  messages = require("../../../nls/setup/deploy/deploy.properties"),
  Promise = require("bluebird"),
  URITemplate = require("../../../lib/shared/uritemplate"),
  xhrPromise = require("../../../lib/client/xhr-promise");
Object.assign = Object.assign || require("object-assign");
var logger = Log.getLogger("model");
var toolchainEndpoint = new URITemplate("{+context}/setup/api/v2/toolchains").expand({
  context: config.CONTEXT_PATH
});

/**
 * Performs the ajax call to create a toolchain from the given capabilities model.
 */
module.exports = /*#__PURE__*/function () {
  function ToolchainCreateClient(options) {
    _classCallCheck(this, ToolchainCreateClient);
    this.workspaceId = options.workspaceId;

    // Region/resource id for the toolchain
    this.regionId = options.regionId;
    this.resourceGroup = options.resourceGroup;
    this.capabilities = options.capabilities;
    this.nocreate = options.nocreate;
    this.repoUrl = options.repo;
    this.template = options.template;
    this.name = options.name;
    this.source = options.source;
    this.marketingTags = options.marketingTags;
    this.enableRg = options.enableRg;
    if (options.enableYmlPreprocessor && options.enableYmlPreprocessor === "true") {
      this.enableYmlPreprocessor = options.enableYmlPreprocessor;
      this.source_provider = options.source_provider;
    }
    this.appId = options.appId; // devex app
    this.appTenant = options.appTenant;
    this.enableSecretPicker = options.enableSecretPicker;
    this.enableSecretDtc = options.enableSecretDtc;
  }
  return _createClass(ToolchainCreateClient, [{
    key: "create",
    value: function create() {
      var servicesMap = this.getServicesPayload(servicesMap);
      var body = {
        toolchain: this.getToolchainPayload(servicesMap),
        services: servicesMap,
        extras: this.getExtrasPayload(),
        enableSecretPicker: this.enableSecretPicker,
        enableSecretDtc: this.enableSecretDtc
      };
      logger.log("Deploying:\n" + JSON.stringify(body));
      if (typeof this.nocreate !== "undefined" || window.testFlags && window.testFlags.nocreate) {
        window.testFlags = window.testFlags || {};
        window.testFlags.deployBody = body;
        return Promise.delay(1000).then(function () {
          var error = new Error("Skip creating toolchain because of 'nocreate' flag");
          error.fake = true;
          throw error;
        });
      }
      return xhrPromise(toolchainEndpoint, {
        method: "POST",
        dataType: "json",
        body: body
      }).then(function (result) {
        var url = result.xhr.getResponseHeader("Location");
        var createdToolchainGuid = new URL(url).pathname.split("/").filter(function (s) {
          return s !== "";
        }).pop();
        return createdToolchainGuid;
      }).catch(function (error) {
        if (error.xhr && error.xhr.status === 502) {
          // wrap plaintext response
          var text = error.body;
          error = {
            body: {
              code: "502",
              status: "error",
              details: text,
              txId: error.xhr.getResponseHeader("X-Global-Transaction-Id")
            }
          };
        }
        if (error.body) {
          Object.assign(error, error.body);
          delete error.body;
          error.icon = true;
          if (!error.title) {
            error.title = messages.get("error.modalTitle");
          }
        }
        throw error;
      });
    }
  }, {
    key: "getServicesPayload",
    value: function getServicesPayload() {
      var servicesMap = Object.create(null);
      var capabilitiesModel = this.capabilities;

      // Start with a skeleton for each service in the template
      var templateServices = this.template.services;
      Object.keys(templateServices).forEach(function (key) {
        var service = templateServices[key];
        servicesMap[key] = {
          service_id: service.service_id
        };
      });

      // ... Then mix in current param values from capabilities model
      capabilitiesModel.forEachType(function (type, capabilities) {
        capabilities.forEach(function (capability) {
          // Note this will skip forms as they have no service representation
          var service = servicesMap[capability.label];
          if (service) {
            service.parameters = capability.parameters;
          }
        });
      });
      return servicesMap;
    }
  }, {
    key: "getToolchainPayload",
    value: function getToolchainPayload(servicesMap) {
      var toolchain = this.capabilities.getToolchain() || {};
      var name = this.name;
      var regionId = this.regionId;
      var toolchainPayload = {
        name: name,
        description: toolchain.description || name,
        generator: toolchain.generator || "otc_setup",
        template: this.getTemplateInfo(servicesMap)
      };
      if (this.enableRg) {
        if (this.resourceGroup) {
          toolchainPayload["container"] = {
            "guid": this.resourceGroup.id,
            "type": "resource_group_id"
          };
          toolchainPayload["key"] = [name, this.resourceGroup.id, regionId].join("_");
        }
      }
      return toolchainPayload;
    }
  }, {
    key: "getTemplateInfo",
    value: function getTemplateInfo(servicesMap) {
      var toolchain = this.capabilities.getToolchain();
      var templateInfo = Object.assign({}, toolchain && toolchain.template);
      templateInfo.services_total = Object.keys(servicesMap).length;
      templateInfo.name = this.template.template.name || undefined; // "Develop a Kubernetes app" etc
      templateInfo.type = "template";
      templateInfo.url = this.repoUrl;
      templateInfo.source = this.source;
      if (this.marketingTags && Object.keys(this.marketingTags).length > 0) {
        templateInfo.campaigns = this.marketingTags;
      }
      return templateInfo;
    }
  }, {
    key: "getExtrasPayload",
    value: function getExtrasPayload() {
      var extras = [];
      if (this.resourceGroup && this.resourceGroup.id && this.enableRg) {
        extras.push({
          type: "cd",
          resourceGroupId: this.resourceGroup.id
        });
      }
      var workspaceId = this.workspaceId;
      if (workspaceId && this.resourceGroup && this.resourceGroup.id) {
        extras.push({
          type: "deployable",
          deployable: {
            deployable_guid: workspaceId,
            type: "schematics_workspace",
            region: this.regionId,
            container: {
              guid: this.resourceGroup.id,
              type: "resource_group_id"
            }
          },
          toolchain: {
            region_id: this.regionId
          }
        });
      }
      var templateData = this.capabilities.templateData;
      if (templateData) {
        var form = templateData.form;
        if (form) {
          var pipeline = form.pipeline;
          if (pipeline) {
            var schema = pipeline.schema;
            if (schema) {
              var schemaProperties = schema.properties;
              if (schemaProperties) {
                var toolchainRegion = this.regionId;
                Object.keys(schemaProperties).forEach(function (propName) {
                  var validatorType = schemaProperties[propName].validator;
                  if (validatorType) {
                    if (validatorType['v-type'] === "k8s-cluster-name") {
                      var propsEnum = schemaProperties[propName].enum,
                        value = schemaProperties[propName].value;
                      if (propsEnum && propsEnum.length && value) {
                        // find the enum entry matching the value
                        var cluster = propsEnum.find(function (i) {
                          return i.name === value;
                        });
                        if (cluster) {
                          var crnId = cluster.crn;
                          var clusterId = cluster.id;
                          var clusterRegion = cluster.region;
                          var clusterRGId = cluster.resourceGroup;
                          var deployableMappingExists = false;
                          for (var index = 0; index < extras.length; index++) {
                            var item = extras[index];
                            if (item && item.deployable && item.deployable.deployable_guid === clusterId && item.deployable.region_id === clusterRegion && item.deployable.container.guid === clusterRGId && item.toolchain && item.toolchain.region_id === toolchainRegion && item.deployable.crn === crnId) deployableMappingExists = true;
                          }
                          if (!deployableMappingExists) {
                            extras.push({
                              type: "deployable",
                              deployable: {
                                deployable_guid: clusterId,
                                type: "kubernetes_cluster",
                                region_id: clusterRegion,
                                container: {
                                  guid: clusterRGId,
                                  type: "resource_group_id"
                                },
                                crn: crnId
                              },
                              toolchain: {
                                region_id: toolchainRegion
                              }
                            });
                          }
                        }
                      }
                    }
                  }
                });
              }
            }
          }
        }
      }
      // The bring-your-own-code devex flow requires that we bind the toolchain we create to the app,
      // so we call them back at the end of the flow, much like how the deployable mapping creates the link
      // for the Enable CD flow.
      var appId = this.appId,
        // devex app
        appTenant = this.appTenant;
      if (appId && appTenant) {
        extras.push({
          type: "app_binding",
          app: {
            id: appId,
            tenant: appTenant
          },
          toolchain: {
            region_id: this.regionId
          }
        });
      }
      return extras;
    }
  }]);
}();